import { openInNewTab } from '../open-in-new-tab';

/**
 * Extract the filename from a `Content-Disposition` header so downloads can use
 * the server as the single source of truth for the filename. Supports both the
 * RFC 5987 `filename*=charset'lang'value` form (preferred) and the plain
 * `filename="..."` form, returning null when no filename is present.
 */
export const getFileNameFromContentDisposition = (
  header: string | null | undefined,
): string | null => {
  if (!header) return null;

  const extendedMatch = /filename\*=([^;]+)/i.exec(header);
  if (extendedMatch?.[1]) {
    try {
      // RFC 5987: `charset'lang'value` — strip the charset/language prefix
      // (the language tag may be empty or set, e.g. `UTF-8''` or `UTF-8'en'`)
      // before percent-decoding the remaining value.
      const encoded = extendedMatch[1]
        .trim()
        .replace(/^"|"$/g, '')
        .replace(/^[^']*'[^']*'/, '');
      return decodeURIComponent(encoded);
    } catch {
      // fall through to the plain filename form
    }
  }

  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || null;
};

export const downloadURL = (url: string, fileName = '') => {
  const link = document.createElement('a');
  link.setAttribute('download', fileName);
  link.setAttribute('href', url);
  link.click();
  link.remove();
};

export const downloadCanvas = (element: HTMLCanvasElement, fileName = '') => openInNewTab(element.toDataURL(), true, fileName);

export const downloadFile = async (src: string, fileName = '', requestOptions?: RequestInit) => {
  if (fetch) {
    await fetch(src, requestOptions)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.statusText);

        return r;
      })
      .then(async (r) => downloadURL(URL.createObjectURL(await r.blob()), fileName));
  }
};

export interface CSVDownloadOptions {
  headers: string[];
  data: any[];
  fileName: string;
  transformRow?: (item: any) => any[];
}

export const downloadCSV = ({
  headers,
  data,
  fileName,
}: CSVDownloadOptions) => {
  if (!data || data.length === 0) {
    return;
  }

  const csvContent = [
    headers.join(','),
    ...data.map((item) => {
      const rowData = Object.values(item);
      return rowData
        .map((value) => {
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value;
        })
        .join(',');
    }),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadFile(URL.createObjectURL(blob), fileName);
};
